import React from "react";
import { Box, useTheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { AccountingHeader } from "./components/Header/accounting.header";
import { Sidebar } from "./components/Sidebar/Sidebar";
import { LogoutDialog } from "./components/LogoutDialog/LogoutDialog";
import { BranchProvider } from "./components/BranchContext";
import { ConfigurationProvider } from "./components/ConfigurationContext";

const drawerWidth = 280;

export const AccountingLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  // Mock user data - replace with actual auth hook
  const user = {
    first_name: "John",
    last_name: "Doe",
    email: "john.doe@example.com",
    business_name: "Accounting Dashboard",
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutCancel = () => {
    if (!isLoggingOut) {
      setLogoutDialogOpen(false);
    }
  };

  const handleLogoutConfirm = async () => {
    try {
      setIsLoggingOut(true);
      // Add actual logout logic here

      navigate("/auth/login");
    } catch (error) {
      console.error("Logout failed:", error);
      setIsLoggingOut(false);
    } finally {
      setLogoutDialogOpen(false);
    }
  };

  return (
    <ConfigurationProvider>
      <BranchProvider>
        <ThemeProvider theme={theme}>
          <Box sx={{ display: "flex", minHeight: "100vh" }}>
            <AccountingHeader
              drawerWidth={drawerWidth}
              onDrawerToggle={handleDrawerToggle}
              onLogout={handleLogoutClick}
              user={user}
              mobileOpen={mobileOpen}
            />
            <Sidebar
              drawerWidth={drawerWidth}
              mobileOpen={mobileOpen}
              onDrawerToggle={handleDrawerToggle}
              onLogout={handleLogoutClick}
              currentPath={location.pathname}
            />
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                width: { sm: `calc(100% - ${drawerWidth}px)` },
                height: { xs: `calc(100vh - 70px)`, md: `calc(100vh - 80px)` },
                mt: { xs: "70px", md: "80px" },
                overflowY: "auto",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? theme.palette.background.default
                    : theme.palette.grey[50],
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ flex: 1, p: { xs: 1, md: 2 } }}>
                <React.Suspense fallback={<div>Loading...</div>}>
                  {children}
                </React.Suspense>
              </Box>
            </Box>
            <LogoutDialog
              open={logoutDialogOpen}
              onClose={handleLogoutCancel}
              onConfirm={handleLogoutConfirm}
              isLoading={isLoggingOut}
            />
          </Box>
        </ThemeProvider>
      </BranchProvider>
    </ConfigurationProvider>
  );
};
